import { BestPracticeDataResponse } from '@deltek/specification-client/core/Models/bestPractice'
import { AllProjectResponse } from 'Models/project'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { shallow } from 'zustand/shallow'
import { ContentItemViewNodesType } from './types/contentItemViewNode.type'
import { HoveredItemStoreType } from './types/hoveredItemStore.type'

/**
 * Creates a zustand store for managing workspace hoveredItem state
 */
export const useHoveredItemStore = create<HoveredItemStoreType>()((set) => ({
    isMainMenuDisplayed: false,
    isBPMenuDisplayed: false,
    isManageOptionsDisplayed: false,
    isNotesMenuDisplayed: false,
    bestPracticeElementData: {} as BestPracticeDataResponse,
    isBestPracticeElementDataLoading: false,
    projectData: {} as Partial<AllProjectResponse>,
    isProjectDataLoading: false,
    hoveredNode: {} as Partial<ContentItemViewNodesType>,
    isProductTypeDeactivationDialogOpen: false,
    isMoveBlockMenuDisplayed: false,
    selectedMainMenuNode: {} as Partial<ContentItemViewNodesType>,
    selectedElementId: undefined,
    dragAnchorElement: null,
    manageOptionSelected: null,
    clashDetectionResponse: null,
    showClashDetectionDialog: false,
    showSustainabilityWarningDialog: false,
    sustainabilityParameters: '',
    ptDeactivationDialogInfo: null,
    /** actions/setters */
    actions: {
        setIsMainMenuDisplayed: (displayed: boolean) => {
            set({ isMainMenuDisplayed: displayed })
        },
        setIsBPMenuDisplayed: (displayed: boolean) => {
            set({ isBPMenuDisplayed: displayed })
        },
        setIsManageOptionsDisplayed: (displayed: boolean) => {
            set({ isManageOptionsDisplayed: displayed })
        },
        setIsNotesMenuDisplayed: (displayed: boolean) => {
            set({ isNotesMenuDisplayed: displayed })
        },
        setBestPracticeElementData: (data: BestPracticeDataResponse) => {
            set({ bestPracticeElementData: data })
        },
        setHoveredNode: (node?: Partial<ContentItemViewNodesType>) => {
            set({ hoveredNode: node })
        },
        setProjectData: (data: AllProjectResponse) => {
            set({ projectData: data })
        },
        setIsProjectDataLoading: (loading: boolean) => {
            set({ isProjectDataLoading: loading })
        },
        setIsBestPracticeElementDataLoading: (loading: boolean) => {
            set({ isBestPracticeElementDataLoading: loading })
        },
        setIsProductTypeDeactivationDialogOpen: (value: boolean) => {
            set({ isProductTypeDeactivationDialogOpen: value })
        },
        setIsMoveBlockMenuDisplayed: (displayed) => {
            set({ isMoveBlockMenuDisplayed: displayed })
        },
        setSelectedMainMenuNode: (node) => {
            set({ selectedMainMenuNode: node })
        },
        setSelectedElementId: (elementId) => {
            set({ selectedElementId: elementId })
        },
        setDragAnchorElement: (element) => {
            set({ dragAnchorElement: element })
        },
        setManageOptionSelected: (type: 'RESET' | 'DEACTIVATE' | null) => {
            set({ manageOptionSelected: type })
        },
        setClashDetectionResponse: (clashDetectionResponse) => {
            set({ clashDetectionResponse })
        },
        setShowClashDetectionDialog: (showClashDetectionDialog) => {
            set({ showClashDetectionDialog })
        },
        setShowSustainabilityWarningDialog: (
            showSustainabilityWarningDialog
        ) => {
            set({ showSustainabilityWarningDialog })
        },
        setSustainabilityParameters: (sustainabilityParameters) => {
            set({ sustainabilityParameters })
        },
        setPtDeactivationDialogInfo: (ptDeactivationDialogInfo) => {
            set({ ptDeactivationDialogInfo })
        },
    },
}))
/** Zustand selector for getting the actions/setters. */
export const useHoveredItemActions = () =>
    useShallowHoveredItems((state) => state.actions)
export const useIsMainMenuDisplayed = () =>
    useShallowHoveredItems((state) => state.isMainMenuDisplayed)
export const useIsBPMenuDisplayed = () =>
    useShallowHoveredItems((state) => state.isBPMenuDisplayed)
export const useIsManageOptionsDisplayed = () =>
    useShallowHoveredItems((state) => state.isManageOptionsDisplayed)
export const useManageOptionSelected = () =>
    useShallowHoveredItems((state) => state.manageOptionSelected)
export const useIsNotesMenuDisplayed = () =>
    useShallowHoveredItems((state) => state.isNotesMenuDisplayed)

export const useBestPracticeElementData = () =>
    useShallowHoveredItems((state) => state.bestPracticeElementData)
export const useIsBestPracticeElementDataLoading = () =>
    useShallowHoveredItems((state) => state.isBestPracticeElementDataLoading)
export const useProjectData = () =>
    useShallowHoveredItems((state) => state.projectData)
export const useIsProjectDataLoading = () =>
    useShallowHoveredItems((state) => state.isProjectDataLoading)
export const useHoveredNode = () =>
    useShallowHoveredItems((state) => state.hoveredNode)
export const useIsProductTypeDeactivationDialogOpen = () =>
    useShallowHoveredItems((state) => state.isProductTypeDeactivationDialogOpen)
export const useIsMoveBlockMenuDisplayed = () =>
    useShallowHoveredItems((state) => state.isMoveBlockMenuDisplayed)
export const useSelectedMainMenuNode = () =>
    useShallowHoveredItems((state) => state.selectedMainMenuNode)
export const useSelectedElementId = () =>
    useShallowHoveredItems((state) => state.selectedElementId)
export const useDragAnchorElement = () =>
    useShallowHoveredItems((state) => state.dragAnchorElement)
/**
 * Zustand selector that gets the clash detection response.
 * @returns The clash detection response from store.
 */
export const useClashDetectionResponse = () =>
    useShallowHoveredItems((state) => state.clashDetectionResponse)

/**
 * Zustand selector that gets the clash detection dialog visibility state.
 * @returns Whether the clash detection dialog is visible.
 */
export const useShowClashDetectionDialog = () =>
    useShallowHoveredItems((state) => state.showClashDetectionDialog)

/**
 * Zustand selector that gets the sustainability warning dialog visibility state.
 * @returns Whether the sustainability warning dialog is visible.
 */
export const useShowSustainabilityWarningDialog = () =>
    useShallowHoveredItems((state) => state.showSustainabilityWarningDialog)

/**
 * Zustand selector that gets the sustainability parameters.
 * @returns The sustainability parameters string.
 */
export const useSustainabilityParameters = () =>
    useShallowHoveredItems((state) => state.sustainabilityParameters)

export const usePtDeactivationDialogInfo = () =>
    useShallowHoveredItems((state) => state.ptDeactivationDialogInfo)

//  * @param selector - A function that takes the store state and returns the value to be selected.
//  * @returns The selected value from the store.

export const useShallowHoveredItems = <T>(
    selector: (state: HoveredItemStoreType) => T
): T => useHoveredItemStore(useShallow(selector))

/**
 * Optimized selector for TreeViewHoveredIcons component that uses shallow comparison
 * to prevent unnecessary re-renders when other parts of the state change.
 */
export const useHoverIconsState = () =>
    useHoveredItemStore(
        (state) => ({
            hoveredNode: state.hoveredNode,
            isMainMenuDisplayed: state.isMainMenuDisplayed,
            selectedMainMenuNode: state.selectedMainMenuNode,
            isManageOptionsDisplayed: state.isManageOptionsDisplayed,
            isBPMenuDisplayed: state.isBPMenuDisplayed,
        }),
        shallow
    )
/**
 * Optimized selector for TreeViewHoveredIcons component that uses shallow comparison
 * to prevent unnecessary re-renders when other parts of the state change.
 */
export const useHoverActionMenuState = () =>
    useHoveredItemStore(
        (state) => ({
            isNotesMenuDisplayed: state.isNotesMenuDisplayed,
            isMoveBlockMenuDisplayed: state.isMoveBlockMenuDisplayed,
        }),
        shallow
    )
